/**
 * Панель треда — правая колонка, включается кнопкой «Тред» в контекстном
 * меню сообщения.
 *
 * Состав: корневое сообщение, лента ответов (ThreadTimeline), композер.
 * Отдельная панель, а не модалка: человек должен видеть основную ленту
 * и тред одновременно, иначе контекст разговора теряется.
 *
 * СОСТОЯНИЕ ОТКРЫТОГО ТРЕДА живёт в RoomView (openThreadId), а не здесь:
 * панель — глупый рендер, данные и действия ей дают снаружи.
 */
import { useCallback, useEffect, useState } from "react";
import { PanelResizeHandle, usePanelWidth } from "./PanelResize";
import { useT, useTF } from "../i18n";
import {
  EventStatus,
  MsgType,
  ReceiptType,
  RoomEvent,
  ThreadEvent,
  type MatrixClient,
  type Room,
  type Thread,
} from "matrix-js-sdk";

import { humanError, membersOf, sendThreadMessage } from "../matrix/roomActions";
import { useRoomPermissions } from "../matrix/permissions";
import { senderLabel } from "../matrix/rooms";
import { replyTargetId, stripFormattedReplyFallback, stripReplyFallback } from "../matrix/relations";
import {
  buildThreadReactions,
  buildThreadTimeline,
  canLoadThreadOlder,
  loadThreadOlder,
  type ReactionChip,
  type TimelineItem,
} from "../matrix/timeline";
import { ThreadComposer } from "./ThreadComposer";
import { ThreadTimeline } from "./ThreadTimeline";
import { TypingIndicator } from "./TypingIndicator";
import { s } from "./styles";
import { color, space } from "./theme";
import { useWindowActive } from "./useWindowActive";

export function ThreadPanel({
  client,
  room,
  rootId,
  thread,
  onClose,
  // onReply снаружи больше не используется: с 14.09 панель треда сама
  // собирает цитату через handleThreadReply, чтобы приложить миниатюру.
  // Проп оставлен в типе ради совместимости вызывающего кода.
  onEdit,
  onDelete,
  onReactionToggle,
}: {
  client: MatrixClient;
  room: Room;
  /** id корневого сообщения. Есть ВСЕГДА — в отличие от самого треда. */
  rootId: string;
  /**
   * Объект треда из SDK или null, если треда ЕЩЁ НЕТ.
   *
   * БАГ, из-за которого это переписано (Антон, 2026-09-10 12:57): раньше
   * RoomView делал `room.getThread(id)` и бросал исключение при null.
   * Но `getThread` возвращает null для ЛЮБОГО сообщения без ответов —
   * то есть при попытке НАЧАТЬ новый тред. Исключение в теле рендера
   * роняло всё приложение в белый экран без выхода: React размонтировал
   * дерево целиком, вместе со списком комнат и кнопкой «закрыть».
   * Тред создаётся сервером в момент ОТПРАВКИ первого ответа, поэтому
   * null здесь — нормальное состояние, а не ошибка.
   */
  thread: Thread | null;
  onClose: () => void;
  onReply?: (eventId: string) => void;
  onEdit?: (eventId: string, currentBody: string) => void;
  onDelete?: (eventId: string) => void;
  onReactionToggle?: (eventId: string, emoji: string) => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [reactions, setReactions] = useState<Map<string, ReactionChip[]>>(new Map());
  const [canLoadOlder, setCanLoadOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<{
    eventId: string;
    senderName: string;
    snippet: string;
    thumbMxc?: string | null;
  } | null>(null);
  const perms = useRoomPermissions(client, room);
  const me = client.getUserId() ?? "";
  const windowActive = useWindowActive();

  /**
   * Объект треда, за которым панель СЛЕДИТ САМА.
   *
   * Проп `thread` от RoomView — снимок на момент рендера. Для НОВОГО треда
   * это null, и после отправки первого ответа он таким и остался бы до тех
   * пор, пока RoomView не перерендерится по своей прихоти: панель висела бы
   * на устаревшем null, подписка ThreadEvent.Update не была бы установлена,
   * и лента не обновлялась (проверка сценария «новый тред» ловила именно
   * это). Поэтому держим живой объект в state и синхронизируем его по
   * RoomEvent.Timeline: когда сервер подтверждает первый ответ, SDK
   * создаёт Thread, и room.getThread(rootId) начинает его возвращать.
   */
  const [liveThread, setLiveThread] = useState<Thread | null>(thread);

  // Проп меняется при каждом рендере RoomView (getThread вызывается в теле
  // рендера) — синхронизируемся, когда объект ДЕЙСТВИТЕЛЬНО другой.
  useEffect(() => {
    setLiveThread(thread);
  }, [thread]);

  useEffect(() => {
    const syncThread = () => {
      const current = room.getThread(rootId) ?? null;
      setLiveThread((prev) => (prev === current ? prev : current));
    };
    syncThread();
    client.on(RoomEvent.Timeline, syncThread);
    return () => {
      client.off(RoomEvent.Timeline, syncThread);
    };
  }, [client, room, rootId]);

  // Дальше панель работает ТОЛЬКО с liveThread: подписки, лента, реакции
  // и отметка прочтения обязаны смотреть на актуальный объект, а не на
  // снимок пропа.
  const effectiveThread = liveThread;

  const refresh = useCallback(() => {
    // Лента строится ВСЕГДА, даже без объекта треда: пока тред не создан,
    // ответ живёт локальным эхом в таймлайне комнаты (см. buildThreadTimeline).
    setItems(buildThreadTimeline(room, rootId, effectiveThread, me));
    // Реакции треда — ТОЛЬКО по его событиям (buildThreadReactions). Брать
    // общую карту комнаты (buildReactions) было дефектом первого захода:
    // она отбрасывает реакции, чья цель живёт в тредовом таймлайне, — то
    // есть РОВНО те, ради которых панель и открывают.
    // Нового треда (thread === null) реакций нет по определению.
    setReactions(effectiveThread ? buildThreadReactions(effectiveThread, me) : new Map());
    setCanLoadOlder(effectiveThread ? canLoadThreadOlder(effectiveThread) : false);
  }, [effectiveThread, room, rootId, me]);

  /**
   * Отметка прочтения ТРЕДА (задача «непрочитанные в тредах»).
   *
   * Отмечаем ТОЛЬКО тред и ТОЛЬКО когда человек реально смотрит панель:
   * она открыта (иначе этого эффекта нет вовсе) и окно в фокусе. Без
   * windowActive получилось бы «свернул браузер с открытым тредом — и всё
   * прочиталось», тот же класс лжи, что RoomView запрещает для комнаты.
   *
   * Посылаем receipt на ПОСЛЕДНИЙ ответ: сервер и Element считают
   * прочитанным тред по максимальной позиции отметки, как в комнате.
   * thread_id в отметку SDK подставляет САМ из события (проверено по
   * src/models/read-receipt.ts v42.3.0: synthesizeReceipt берёт
   * threadIdForReceipt(event)) — третий аргумент sendReadReceipt в 42.3.0
   * это boolean `unthreaded`, а не threadId, и передавать туда id треда
   * было бы тихой ошибкой типа.
   *
   * Защита от спама — hasUserReadEvent на объекте Thread (у него свои
   * тредовые receipts): повторная отправка на каждый рендер не уйдёт.
   * Ошибку глушим сознательно и отличаемся от комнатной плашки: отказ
   * тредовой отметки ничего не ломает визуально (счётчик комнаты всё равно
   * придёт с сервера), а плашка ошибки в узкой панели съела бы пол-экрана.
   * В консоль пишем, чтобы инцидент можно было разобрать.
   */
  useEffect(() => {
    if (!effectiveThread || !windowActive) return;
    const last = effectiveThread.lastReply();
    const lastId = last?.getId();
    if (!last || !lastId || lastId.startsWith("~")) return;
    if (last.getSender() === me) return;
    if (effectiveThread.hasUserReadEvent(me, lastId)) return;
    client
      .sendReadReceipt(last, ReceiptType.Read)
      .catch((e) => console.warn("thread receipt failed", e));
  }, [client, effectiveThread, me, windowActive, items]);

  // Первая загрузка: SDK может не иметь локального эха треда, если мы
  // только что открыли его из контекстного меню. Просим SDK догрузить
  // тред с сервера (initialThreadSync), иначе панель покажет пустую ленту.
  useEffect(() => {
    refresh();

    // События, при которых лента треда могла измениться:
    //  - ThreadEvent.Update — новый ответ, догрузка, локальное эхо;
    //  - RoomEvent.Timeline — корень треда пришёл в главный таймлайн;
    //  - RoomEvent.LocalEchoUpdated — наше сообщение подтверждено/упало.
    const onThreadUpdate = (t: Thread) => {
      if (t.id === rootId) refresh();
    };
    const onRoomTimeline = () => refresh();
    const onEcho = () => refresh();

    // ГОЧА (matrix-js-sdk 42.3.0): ThreadEvent.Update НЕ входит в
    // EmittedEvents клиента — событие живёт на самом Thread и наверх не
    // пробрасывается. Поэтому подписываемся на объект треда, а не на
    // client; иначе обработчик молча никогда не вызовется (и tsc это
    // как раз и поймал).
    // Треда может не быть (новый тред) — тогда подписываться не на что,
    // но RoomEvent.Timeline слушаем всё равно: именно оттуда придёт
    // созданный тред после отправки первого ответа.
    effectiveThread?.on(ThreadEvent.Update, onThreadUpdate);
    client.on(RoomEvent.Timeline, onRoomTimeline);
    client.on(RoomEvent.LocalEchoUpdated, onEcho);

    return () => {
      effectiveThread?.off(ThreadEvent.Update, onThreadUpdate);
      client.off(RoomEvent.Timeline, onRoomTimeline);
      client.off(RoomEvent.LocalEchoUpdated, onEcho);
    };
  }, [client, effectiveThread, rootId, refresh]);

  // Догрузка истории треда вверх.
  const onLoadOlder = useCallback(async () => {
    if (loadingOlder || !effectiveThread) return;
    setLoadingOlder(true);
    setError(null);
    try {
      await loadThreadOlder(client, effectiveThread, 40);
      refresh();
    } catch (e) {
      setError(humanError(e));
    } finally {
      setLoadingOlder(false);
    }
  }, [client, effectiveThread, loadingOlder, refresh]);

  // Отправка ответа в тред.
  const handleSend = useCallback(
    async (segments: Parameters<typeof sendThreadMessage>[3]) => {
      setSendError(null);
      try {
        // ПО rootId, а не thread.id: при создании нового треда объекта
        // треда ещё нет, а корень известен. Сервер создаёт тред из
        // rel_type=m.thread в первом же ответе.
        await sendThreadMessage(client, room.roomId, rootId, segments, replyTo?.eventId);
        setReplyTo(null);
      } catch (e) {
        setSendError(humanError(e));
        throw e; // ThreadComposer покажет ошибку в своём поле
      }
    },
    [client, room.roomId, rootId, replyTo],
  );

  const handleThreadReply = useCallback(
    (eventId: string) => {
      const item = items.find((i) => i.eventId === eventId);
      if (!item) return;
      const thumbMxc =
        item.attachment &&
        (item.attachment.msgtype === "m.image" || item.attachment.msgtype === "m.video")
          ? item.attachment.mxc
          : null;
      setReplyTo({
        eventId,
        senderName: item.senderName,
        snippet: item.body.slice(0, 100),
        thumbMxc,
      });
    },
    [items],
  );

  // Корневое сообщение треда: ищем в ГЛАВНОМ таймлайне, а не в тредовом —
  // thread.liveTimeline.getEvents() содержит ТОЛЬКО ответы (проверено по
  // lib/models/thread.ts: фильтр rel_type == RelationType.Thread).
  const rootEvent = room.getLiveTimeline().getEvents().find((ev) => ev.getId() === rootId);
  // Собираем TimelineItem для корня тем же способом, что buildTimeline,
  // но без системных событий и без группировки: корень всегда один.
  const rootItem: TimelineItem | null = rootEvent
    ? {
        eventId: rootEvent.getId() ?? "",
        sender: rootEvent.getSender() ?? "",
        senderId: rootEvent.getSender() ?? "",
        senderName: senderLabel(room, rootEvent),
        ts: rootEvent.getTs(),
        // Корень треда мог сам быть ответом: срезаем fallback-цитату так же,
        // как это делает buildTimeline, иначе шапка панели покажет «> <@…>».
        body: stripReplyFallback(
          String(rootEvent.getContent().body ?? ""),
          replyTargetId(rootEvent) !== null,
        ),
        formattedBody:
          typeof rootEvent.getContent().formatted_body === "string"
            ? stripFormattedReplyFallback(
                rootEvent.getContent().formatted_body as string,
                replyTargetId(rootEvent) !== null,
              )
            : null,
        attachment: null,
        emote: rootEvent.getContent().msgtype === MsgType.Emote,
        pending: rootEvent.status === EventStatus.SENDING || rootEvent.status === EventStatus.QUEUED,
        failed: rootEvent.status === EventStatus.NOT_SENT,
        edited: Boolean(rootEvent.replacingEvent()),
        mentionsMe: false, // корень в треде не подсвечиваем: он уже открыт
        startsGroup: true,
        replyTo: null,
        threadRootId: null,
        thread: null, // внутри панели индикатор не нужен: мы уже в треде
        system: null,
        // Корень треда — всегда обычное сообщение; карточек звонка в тредах
        // нет (звонок принадлежит комнате, не ветке обсуждения).
        call: null,
        // Для корня треда упоминания не собираем: они уже учтены в ленте
        // комнаты. Пустой массив, чтобы не тащить лишнюю логику сюда.
        mentions: null,
        // Корень треда тоже можно удалить — тогда панель показывает надгробие,
        // а не пустую шапку без текста (у redacted события `body` пуст).
        redacted: rootEvent.isRedacted(),
      }
    : null;

  const [panelWidth, setPanelWidth] = usePanelWidth("threadPanel", 340, 260, 720);

  return (
    <aside
      style={{ ...s.threadPanel, width: panelWidth, flex: `0 0 ${panelWidth}px`, position: "relative" }}
      data-testid="thread-panel"
      aria-label={t("thread.title")}
      /* alg-thread-panel: на ≤640px мобильный CSS делает панель fixed на
         весь экран поверх ленты (спека 2026-09-20). */
      className="alg-thread-panel"
    >
      <PanelResizeHandle
        edge="left"
        width={panelWidth}
        onResize={setPanelWidth}
        onReset={() => setPanelWidth(340)}
        testId="resize-thread-panel"
      />
      <div
        style={{
          ...s.roomHeader,
          display: "flex",
          alignItems: "center",
          gap: space.md,
        }}
      >
        <div style={{ flex: "1 1 auto", fontSize: 14, fontWeight: 600 }}>
          {t("thread.title")}
          {(effectiveThread?.length ?? 0) > 0 && (
            <span style={{ color: color.textDim, fontWeight: 400 }}> · {effectiveThread?.length ?? 0}</span>
          )}
        </div>
        <button
          type="button"
          style={s.actionButton}
          onClick={onClose}
          data-testid="thread-close"
          title={t("ui.zakryt_tred")}
        >
          ✕
        </button>
      </div>

      {error && (
        <div style={{ padding: "6px 16px", fontSize: 12, color: color.danger }}>
          {tfmt("thread.historyError", { error })}
        </div>
      )}
      {sendError && (
        <div style={{ padding: "6px 16px", fontSize: 12, color: color.danger }}>
          {tfmt("thread.replyNotSent", { error: sendError })}
        </div>
      )}

      <ThreadTimeline
        client={client}
        room={room}
        rootId={rootId}
        thread={effectiveThread}
        rootItem={rootItem}
        items={items}
        reactions={reactions}
        canLoadOlder={canLoadOlder}
        loadingOlder={loadingOlder}
        onLoadOlder={onLoadOlder}
        onReply={handleThreadReply}
        onEdit={onEdit}
        onDelete={onDelete}
        onReactionToggle={onReactionToggle}
      />

      {/* Индикатор набора общий на комнату (протокол не различает тред):
          показываем ту же строку и здесь (Антон, 2026-09-16 21:27 UTC). */}
      <TypingIndicator client={client} room={room} />

      <ThreadComposer
        disabled={!perms.canSendMessage}
        threadId={rootId}
        roomId={room.roomId}
        client={client}
        members={membersOf(client, room)}
        canMentionRoom={perms.canMentionRoom}
        onSend={handleSend}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
      />
    </aside>
  );
}
